// Structured element capture — the pure part of the picker. Extracted from the résumé site's
// bugPicker.ts; the component map, version global, and magic-link param stripping are now config.
import type { ElementContext } from "@redliner/protocol";
import type { ResolvedConfig } from "./config.js";

export function componentFor(el: Element, cfg: ResolvedConfig): string | null {
  for (let cur: Element | null = el; cur && cur !== document.body; cur = cur.parentElement) {
    const stamped = cur.getAttribute(cfg.componentAttr);
    if (stamped) return stamped;
    for (const cls of cur.classList) {
      const hit = cfg.componentPrefixes.find(([p]) => cls === p || cls.startsWith(p));
      if (hit) return hit[1];
    }
  }
  return null;
}

export function cssPath(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur !== document.body && parts.length < 5) {
    if (cur.id) {
      parts.unshift(`#${cur.id}`);
      break;
    }
    const cls = [...cur.classList].find((c) => !c.includes(":"));
    let part = cur.tagName.toLowerCase() + (cls ? `.${cls}` : "");
    const parent = cur.parentElement;
    if (parent) {
      const same = [...parent.children].filter((c) => c.tagName === cur!.tagName);
      if (same.length > 1) part += `:nth-of-type(${same.indexOf(cur) + 1})`;
    }
    parts.unshift(part);
    cur = cur.parentElement;
  }
  return parts.join(" > ");
}

// Never let single-use secrets (magic-link tokens and friends) land in a work-order record.
export function safeRoute(cfg: ResolvedConfig): string {
  const params = new URLSearchParams(location.search);
  for (const p of cfg.stripParams) params.delete(p);
  const q = params.toString();
  return location.pathname + (q ? `?${q}` : "");
}

export function buildElementContext(el: Element, cfg: ResolvedConfig): ElementContext {
  const r = el.getBoundingClientRect();
  return {
    route: safeRoute(cfg),
    component: componentFor(el, cfg),
    selector: cssPath(el),
    tag: el.tagName.toLowerCase(),
    text: (el.textContent ?? "").trim().slice(0, 80) || null,
    rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dpr: window.devicePixelRatio,
    theme: document.documentElement.getAttribute("data-theme"),
    ua: navigator.userAgent,
    version: cfg.app.version,
    at: new Date().toISOString(),
  };
}

/** Short human label for the hover chip: `GraphPanel · button.icon-btn`. */
export function describe(el: Element, cfg: ResolvedConfig): string {
  const comp = componentFor(el, cfg);
  const cls = [...el.classList].find((c) => !c.includes(":"));
  const self = el.tagName.toLowerCase() + (cls ? `.${cls}` : "");
  return comp ? `${comp} · ${self}` : self;
}
